import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import * as snackbarActions from '../../../actions/snackbarActions';
import MainSnackbar from '../../Utils/Snackbar/SmsSnackbar';
import PageTitle from '../../Utils/smsTitle';
import { SubHeader } from '../../../Containers/SubHeader';
import { BackButton } from '../../Utils/Buttons/DataExportationButton';
import { PatientInformation } from '../../DoctorsModule/PatientInfo/PatientInformation';
import { DoctorAdmissionForm } from '../../DoctorsModule/Admission/DoctorAdmission';
import { useAddAdmission } from '../../Visit/NewAdmission/hooks/useAddAdmission';
import { FullScreenDialog } from '../../Utils/Dialogs/FullScreenDialog';
import { AssignBed } from '../../BedManager/AddEditBedAssign';
import { ConfirmationDialog } from '../../Utils/ReusableComponents/ConfirmationDialog';
import CustomDialog from '../../Utils/Dialogs/CustomDialog';
import PrintDialog from '../../Utils/Dialogs/PrintDialog';
import { CenteredContainer } from '../../Utils/styledComponents';
import { ProviderForm } from '../../Visit/NewVisit/NewVisit';
import { surgeryService } from '../../../services/surgeryService';
import { StatusBadge, PathwayBadge, BillingStatusBadge } from '../shared';
import {
  SURGERY_STATUS,
  BILLING_STATUS_DOT_COLORS,
  OPERATION_CLASS_LABELS,
  ANAESTHESIA_TYPE_LABELS,
  PATHWAY_LABELS,
} from '../shared/constants';
import BillingSection from '../Billing/BillingSection';
import PreOpReadinessStrip from '../PreOp/PreOpReadinessStrip';
import PreOpAssessmentForm from '../PreOp/PreOpAssessmentForm';
import ConsentCapture from '../PreOp/ConsentCapture';
import AnaesthesiaRecord from '../IntraOp/AnaesthesiaRecord';
import OperativeNotesForm from '../IntraOp/OperativeNotesForm';
import ConsumablesTracker from '../IntraOp/ConsumablesTracker';
import PACURecord from '../PostOp/PACURecord';
import PostOpOrders from '../PostOp/PostOpOrders';
import ComplicationReport from '../PostOp/ComplicationReport';

// ─── Styles (matching ReusableDoctorTabs) ───────────────────────────────────────
const useStyles = makeStyles(() => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  // ── Sidebar — same as ReusableDoctorTabs ──────────
  sidebarList: {
    width: '100%',
    paddingTop: 0,
    paddingBottom: 0,
  },
  sidebarItem: {
    paddingTop: 10,
    paddingBottom: 10,
    cursor: 'pointer',
    borderBottom: '1px solid #e8e8e8',
    textTransform: 'capitalize',
    '&:hover': {
      borderRight: '2px solid #3f51b5',
      color: '#3f51b5',
      background: 'rgba(14, 118, 186, .08)',
    },
  },
  sidebarItemActive: {
    borderRight: '2px solid #3f51b5',
    color: '#3f51b5',
  },
  sidebarText: {
    fontSize: '13.86px',
  },
  sidebarCol: {
    overflow: 'auto',
    height: '100vh',
    '&::-webkit-scrollbar': {
      width: 5,
      height: 5,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#c7c6c7',
      borderRadius: 10,
    },
  },
  contentCol: {
    borderLeft: '1px solid #c7c6c7',
  },
  contentInner: {
    padding: 20,
  },
  // ── Content ───────────────────────────────────────
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
    marginTop: 8,
  },
  // ── Sidebar billing dot indicator ────────────────
  sidebarItemWithDot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  billingDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
    marginRight: 8,
  },
  // ── Overview section ──────────────────────────────
  overviewGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    marginTop: 16,
  },
  overviewCard: {
    border: '1px solid #E8E8E8',
    borderRadius: 6,
    padding: '16px 20px',
    backgroundColor: '#fff',
  },
  overviewCardFull: {
    border: '1px solid #E8E8E8',
    borderRadius: 6,
    padding: '16px 20px',
    backgroundColor: '#fff',
    gridColumn: '1 / -1',
  },
  cardTitle: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#9E9E9E',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 12,
  },
  fieldRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: '5px 0',
    '&:not(:last-child)': {
      borderBottom: '1px solid #F5F5F5',
    },
  },
  fieldLabel: {
    fontSize: '0.82rem',
    color: '#757575',
    flexShrink: 0,
    marginRight: 12,
  },
  fieldValue: {
    fontSize: '0.82rem',
    color: '#212121',
    fontWeight: 500,
    textAlign: 'right',
  },
  teamGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0 24px',
  },
  emptyValue: {
    color: '#BDBDBD',
    fontStyle: 'italic',
    fontWeight: 400,
  },
  notesText: {
    fontSize: '0.82rem',
    color: '#424242',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
  },
  // ── Intra-Op Tabs ──────────────────────────────
  intraOpTabs: {
    display: 'flex',
    borderBottom: '2px solid #EEEEEE',
    marginBottom: 16,
    gap: 0,
  },
  intraOpTab: {
    padding: '10px 20px',
    fontSize: '0.82rem',
    fontWeight: 500,
    color: '#757575',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    marginBottom: -2,
    cursor: 'pointer',
    transition: 'color 0.15s, border-color 0.15s',
    '&:hover': {
      color: '#424242',
    },
  },
  intraOpTabActive: {
    color: '#1976D2',
    borderBottomColor: '#1976D2',
    fontWeight: 600,
  },
}));

// ─── Sidebar items ──────────────────────────────────────────────────────────────
const SIDEBAR_ITEMS = [
  { key: 'overview', label: 'Overview' },
  { key: 'billing', label: 'Billing' },
  { key: 'admission', label: 'Admission' },
  { key: 'preop', label: 'Pre-Op' },
  { key: 'intraop', label: 'Intra-Op' },
  { key: 'postop', label: 'Post-Op' },
];

// ─── Helpers ────────────────────────────────────────────────────────────────────
const parseDuration = (durStr) => {
  if (!durStr) return { hr: 0, mins: 0 };
  const parts = String(durStr).split(':');
  return { hr: parseInt(parts[0], 10) || 0, mins: parseInt(parts[1], 10) || 0 };
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return dateStr; }
};

const formatTime = (timeStr) => {
  if (!timeStr) return null;
  const parts = String(timeStr).split(':');
  if (parts.length < 2) return timeStr;
  const h = parseInt(parts[0], 10);
  const m = parts[1];
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${m} ${ampm}`;
};

const formatDuration = (durStr) => {
  const { hr, mins } = parseDuration(durStr);
  if (!hr && !mins) return null;
  const parts = [];
  if (hr) parts.push(`${hr}h`);
  if (mins) parts.push(`${mins}m`);
  return parts.join(' ');
};

const trimName = (name) => {
  if (!name) return null;
  const s = String(name).trim();
  return (s && s !== 'null null' && s !== 'None None') ? s : null;
};

const enrichSurgery = (surgery) => {
  let finalStatus = surgery.status !== undefined
    ? parseInt(surgery.status, 10)
    : (surgery.status_code !== undefined ? parseInt(surgery.status_code, 10) : undefined);
  if (isNaN(finalStatus) || finalStatus === undefined) {
    finalStatus = SURGERY_STATUS.PENDING;
  }

  const dur = parseDuration(surgery.estimated_duration);
  const patientName = surgery.patient_name ||
    `${surgery.first_name || ''} ${surgery.last_name || ''}`.trim() || 'Unknown Patient';

  return {
    ...surgery,
    status_code: finalStatus,
    patient_name: patientName,
    patient_number: surgery.patient_number || surgery.patient_no || '',
    procedure: surgery.procedure || surgery.service_name || '',
    scheduled_date: surgery.scheduled_date || surgery.operation_date || '',
    pathway: surgery.pathway || 'inpatient',
    operation_class: surgery.operation_class || 'Minor',
    doctor_type: 'internal',
    theatre: surgery.theatre || surgery.theatre_name || '',
    duration_hr: dur.hr,
    duration_mins: dur.mins,
  };
};

// =============================================================================
// COMPONENT
// =============================================================================
const SurgeryDetailPage = ({ match, actions, snackbars, location }) => {
  const classes = useStyles();
  const [surgery, setSurgery] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [consentDone, setConsentDone] = useState(false);
  const [preOpDone, setPreOpDone] = useState(false);
  const [preOpStep, setPreOpStep] = useState(0);
  const [intraOpStep, setIntraOpStep] = useState(0);
  const [postOpStep, setPostOpStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sectionAlert, setSectionAlert] = useState('');
  const [readinessKey, setReadinessKey] = useState(0); 

  const handleRefreshReadiness = () => {
    setReadinessKey((prev) => prev + 1);
  };

  const surgeryId = match && match.params ? match.params.surgeryId : null;

  // Handle ?section=billing query param (from surgery list billing actions)
  useEffect(() => {
    if (location && location.search) {
      const params = new URLSearchParams(location.search);
      const section = params.get('section');
      if (section && SIDEBAR_ITEMS.some((item) => item.key === section)) {
        setActiveSection(section);
      }
    }
  }, [location]);

  useEffect(() => {
    if (preOpDone && surgeryId && surgery && surgery.status_code < SURGERY_STATUS.PRE_OP_COMPLETE) {
      setSurgery((s) => ({ ...s, status_code: SURGERY_STATUS.PRE_OP_COMPLETE }));
      surgeryService.startSurgery({ surgery_id: surgeryId, status: SURGERY_STATUS.PRE_OP_COMPLETE }).catch(() => {});
    }
  }, [preOpDone, surgeryId, surgery]);

  const refreshSurgery = useCallback(() => {
    if (!surgeryId) return;
    surgeryService.viewSurgery({ surgery_id: surgeryId }).then((surgeryRes) => {
      const enriched = enrichSurgery(surgeryRes.data);
      setSurgery(enriched);
      if (enriched.has_consent || enriched.status_code >= SURGERY_STATUS.PRE_OP_COMPLETE) {
        setConsentDone(true);
      }
      if (enriched.status_code >= SURGERY_STATUS.PRE_OP_COMPLETE) {
        setPreOpDone(true);
      }
    }).catch(() => {});
  }, [surgeryId]);

  useEffect(() => {
    if (!surgeryId) return;
    let cancelled = false;
    setLoading(true);

    surgeryService.viewSurgery({ surgery_id: surgeryId }).then((surgeryRes) => {
      if (cancelled) return;
      const enriched = enrichSurgery(surgeryRes.data);
      setSurgery(enriched);
      if (enriched.has_consent || enriched.status_code >= SURGERY_STATUS.PRE_OP_COMPLETE) {
        setConsentDone(true);
      }
      if (enriched.status_code >= SURGERY_STATUS.PRE_OP_COMPLETE) {
        setPreOpDone(true);
      }
      setLoading(false);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [surgeryId]);

  // ── Loading / not found ───────────────────────────
  if (loading) {
    return (
      <div>
        <PageTitle title="Surgery Detail" />
        <SubHeader title="Surgery" subTitle="Surgery Detail" />
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (!surgery) {
    return (
      <div>
        <PageTitle title="Surgery Detail" />
        <SubHeader title="Surgery" subTitle="Surgery Detail" />
        <Paper style={{ margin: 22, textAlign: 'center', padding: 40 }}>
          <Typography variant="h6" style={{ color: '#999' }}>Surgery not found</Typography>
        </Paper>
      </div>
    );
  }

  const statusCode = surgery.status_code;

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div>
      <PageTitle title="Surgery Detail" />
      <SubHeader title="Surgery" subTitle="Surgery Detail" />

      {/* Back button — same as doctor module */}
      <div className="general-ledger-header mb-0">
        <BackButton text="surgery list" to="/surgery/list" />
      </div>

      {/* ═══════════════ SIDEBAR + CONTENT (2/10 grid like doctor module) ═══════════════ */}
      <Grid container spacing={0}>
        {/* Sidebar */}
        <Grid item xs={4} md={2} sm={2} lg={2} className={classes.sidebarCol}>
          {SIDEBAR_ITEMS.map((item) => {
            const isBillingItem = item.key === 'billing';
            const isAdmissionItem = item.key === 'admission';
            const billingStatus = surgery ? surgery.billing_status : null;
            const dotColor = isBillingItem && billingStatus
              ? (BILLING_STATUS_DOT_COLORS[billingStatus] || '#9E9E9E')
              : isAdmissionItem && surgery && surgery.patient_admission_id
              ? '#4CAF50'
              : null;

            return (
              <List className={classes.sidebarList} key={item.key}>
                <ListItem
                  className={`${classes.sidebarItem} ${activeSection === item.key ? classes.sidebarItemActive : ''}`}
                  onClick={() => setActiveSection(item.key)}
                >
                  <ListItemText
                    primary={isAdmissionItem && surgery && surgery.patient_admission_id ? 'Admitted' : item.label}
                    classes={{ primary: classes.sidebarText }}
                  />
                  {dotColor && (
                    <span
                      className={classes.billingDot}
                      style={{ backgroundColor: dotColor }}
                    />
                  )}
                </ListItem>
              </List>
            );
          })}
        </Grid>

        {/* Content */}
        <Grid item sm={10} xs={8} md={10} lg={10} className={classes.contentCol}>
          <div className={classes.contentInner}>
            {/* ── Patient Information (shown on every section, like doctor module) ── */}
            <PatientInformation
              patient_number={surgery.patient_number}
              visit_id={surgery.visit_id}
              isChronic={false}
            />

            <Divider style={{ marginBottom: 16 }} />

            {sectionAlert && (
              <div className="alert alert-warning alert-dismissible" role="alert" style={{ marginBottom: 16 }}>
                {sectionAlert}
                <button type="button" className="close" onClick={() => setSectionAlert('')}>
                  <span>&times;</span>
                </button>
              </div>
            )}

            {/* ── Overview ──────────────────────────────── */}
            {activeSection === 'overview' && (
              <OverviewSection surgery={surgery} statusCode={statusCode} classes={classes} />
            )}

            {/* ── Billing ──────────────────────────────── */}
            {activeSection === 'billing' && (
              <BillingSection surgeryId={surgeryId} surgery={surgery} />
            )}

            {/* ── Admission (same form as doctor module) ── */}
            {activeSection === 'admission' && (
              <AdmissionSection
                actions={actions}
                snackbars={snackbars}
                patient_number={surgery.patient_number}
                visit_id={surgery.visit_id}
                surgeryId={surgeryId}
                existingAdmissionId={surgery.patient_admission_id}
                onAdmissionLinked={refreshSurgery}
              />
            )}

            {/* ── Pre-Op (Assessment + Consent) ── */}
            {activeSection === 'preop' && (
              <div>
                <PreOpReadinessStrip surgeryId={surgeryId} refreshKey={readinessKey} />

                <div className={classes.intraOpTabs}>
                  {[
                    { key: 0, label: 'Assessment' },
                    { key: 1, label: 'Consent' },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={`${classes.intraOpTab} ${preOpStep === tab.key ? classes.intraOpTabActive : ''}`}
                      onClick={() => setPreOpStep(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {preOpStep === 0 && (
                  <PreOpAssessmentForm
                    surgeryId={surgeryId}
                    pathway={surgery.pathway}
                    operationClass={surgery.operation_class}
                    readOnly={statusCode >= SURGERY_STATUS.STARTED}
                    onSave={handleRefreshReadiness}
                  />
                )}
                {preOpStep === 1 && (
                  <ConsentCapture
                    surgeryId={surgeryId}
                    procedureName={surgery.service_name || surgery.procedure}
                    surgeonName={surgery.lead_surgeon}
                    patientName={surgery.patient_name}
                    onSave={() => {
                      setConsentDone(true);
                      handleRefreshReadiness(); 
                    }}
                    readOnly={consentDone}
                  />
                )}
              </div>
            )}

            {/* ── Intra-Op ─────────────────────────────── */}
            {activeSection === 'intraop' && (
              <div>
                <div className={classes.intraOpTabs}>
                  {[
                    { key: 0, label: 'Anaesthesia & Vitals' },
                    { key: 1, label: 'Operative Notes' },
                    { key: 2, label: 'Consumables' },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={`${classes.intraOpTab} ${intraOpStep === tab.key ? classes.intraOpTabActive : ''}`}
                      onClick={() => setIntraOpStep(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {intraOpStep === 0 && (
                  <AnaesthesiaRecord
                    surgeryId={surgeryId}
                    patientInfo={{ patient_name: surgery.patient_name }}
                  />
                )}
                {intraOpStep === 1 && (
                  <OperativeNotesForm
                    surgeryId={surgeryId}
                    surgeryData={{
                      procedure: surgery.procedure,
                      surgeon_name: surgery.lead_surgeon,
                      diagnosis: surgery.preoperative_diagnosis,
                      anaesthesia_type: surgery.anaesthesia_type,
                      anaesthetist_name: surgery.lead_anaestheist,
                    }}
                  />
                )}
                {intraOpStep === 2 && (
                  <ConsumablesTracker surgeryId={surgeryId} />
                )}
              </div>
            )}

            {/* ── Post-Op ──────────────────────────────── */}
            {activeSection === 'postop' && (
              <div>
                <div className={classes.intraOpTabs}>
                  {[
                    { key: 0, label: 'PACU Recovery' },
                    { key: 1, label: 'Post-Op Orders' },
                    { key: 2, label: 'Complications' },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={`${classes.intraOpTab} ${postOpStep === tab.key ? classes.intraOpTabActive : ''}`}
                      onClick={() => setPostOpStep(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {postOpStep === 0 && (
                  <PACURecord
                    surgeryId={surgeryId}
                    procedure={surgery.service_name || surgery.procedure}
                    anaesthesiaType={surgery.anaesthesia_type}
                  />
                )}
                {postOpStep === 1 && (
                  <PostOpOrders surgeryId={surgeryId} />
                )}
                {postOpStep === 2 && (
                  <ComplicationReport surgeryId={surgeryId} />
                )}
              </div>
            )}

          </div>
        </Grid>
      </Grid>

    </div>
  );
};

// =============================================================================
// OVERVIEW SECTION
// =============================================================================
const FieldRow = ({ label, value, classes }) => (
  <div className={classes.fieldRow}>
    <span className={classes.fieldLabel}>{label}</span>
    <span className={`${classes.fieldValue} ${!value ? classes.emptyValue : ''}`}>
      {value || '--'}
    </span>
  </div>
);

const OverviewSection = ({ surgery, statusCode, classes }) => {
  const leadSurgeon = trimName(surgery.lead_surgeon_name) || trimName(surgery.lead_surgeon);
  const leadAnaesthetist = trimName(surgery.lead_anaesthetist_name) || trimName(surgery.lead_anaestheist);
  const scrubNurse = trimName(surgery.scrub_nurse);
  const circNurse = trimName(surgery.circulating_nurse);
  const theatreName = surgery.theatre_name || surgery.theatre;
  const opClass = OPERATION_CLASS_LABELS[surgery.operation_class] || surgery.operation_class;
  const anaesthType = ANAESTHESIA_TYPE_LABELS[surgery.anaesthesia_type] || surgery.anaesthesia_type;
  const pathwayLabel = PATHWAY_LABELS[surgery.pathway] || surgery.pathway;

  return (
    <div>
      {/* Status row */}
      <div className={classes.statusRow}>
        <StatusBadge status={statusCode} />
        <PathwayBadge pathway={surgery.pathway} />
        {surgery.billing_status && <BillingStatusBadge status={surgery.billing_status} />}
      </div>

      <div className={classes.overviewGrid}>
        {/* ── Procedure ──────────────────────── */}
        <div className={classes.overviewCard}>
          <div className={classes.cardTitle}>Procedure</div>
          <FieldRow label="Name" value={surgery.service_name || surgery.procedure} classes={classes} />
          <FieldRow label="Department" value={surgery.department_name} classes={classes} />
          <FieldRow label="Operation Class" value={opClass} classes={classes} />
          <FieldRow label="Pathway" value={pathwayLabel} classes={classes} />
        </div>

        {/* ── Schedule ───────────────────────── */}
        <div className={classes.overviewCard}>
          <div className={classes.cardTitle}>Schedule</div>
          <FieldRow label="Date" value={formatDate(surgery.operation_date)} classes={classes} />
          <FieldRow label="Start Time" value={formatTime(surgery.estimated_start_time)} classes={classes} />
          <FieldRow label="Duration" value={formatDuration(surgery.estimated_duration)} classes={classes} />
          <FieldRow label="Theatre" value={theatreName} classes={classes} />
        </div>

        {/* ── Surgical Team ──────────────────── */}
        <div className={classes.overviewCardFull}>
          <div className={classes.cardTitle}>Surgical Team</div>
          <div className={classes.teamGrid}>
            <FieldRow label="Lead Surgeon" value={leadSurgeon} classes={classes} />
            <FieldRow label="Anaesthetist" value={leadAnaesthetist} classes={classes} />
            <FieldRow label="Scrub Nurse" value={scrubNurse} classes={classes} />
            <FieldRow label="Circulating Nurse" value={circNurse} classes={classes} />
          </div>
        </div>

        {/* ── Anaesthesia ────────────────────── */}
        <div className={classes.overviewCard}>
          <div className={classes.cardTitle}>Anaesthesia</div>
          <FieldRow label="Type" value={anaesthType} classes={classes} />
        </div>

        {/* ── Scheduling Info ────────────────── */}
        <div className={classes.overviewCard}>
          <div className={classes.cardTitle}>Record</div>
          <FieldRow label="Scheduled By" value={trimName(surgery.created_by_name)} classes={classes} />
          <FieldRow label="Date Created" value={formatDate(surgery.date_added)} classes={classes} />
        </div>

        {/* ── Pre-operative Diagnosis ────────── */}
        {surgery.preoperative_diagnosis && (
          <div className={classes.overviewCardFull}>
            <div className={classes.cardTitle}>Pre-operative Diagnosis</div>
            <div className={classes.notesText}>{surgery.preoperative_diagnosis}</div>
          </div>
        )}

        {/* ── Pre-operative Instructions ─────── */}
        {surgery.preoperative_instructions && (
          <div className={classes.overviewCardFull}>
            <div className={classes.cardTitle}>Pre-operative Instructions</div>
            <div className={classes.notesText}>{surgery.preoperative_instructions}</div>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// ADMISSION SECTION — uses the same form & hook as doctor module
// =============================================================================
// const AdmissionSection = ({ actions, snackbars, patient_number, visit_id, surgeryId, existingAdmissionId, onAdmissionLinked }) => {
//   const admissionMatch = useMemo(
//     () => ({ patient_number, visit_id, patient_admission_id: existingAdmissionId || null }),
//     [patient_number, visit_id, existingAdmissionId]
//   );

//   // const {
//   //   admissionProps, closeSnackbar, printDialog, handleClosePrintDialog, handleClick,
//   //   assignProps, openDialog, handleCloseDialog, openAssignBed,
//   //   handleOpenAssignBed, handleCloseAssignBed, patient_admission_id
//   // } = useAddAdmission(actions, patient_number, false, admissionMatch);
// const {
//   admissionProps, closeSnackbar, printDialog, handleClosePrintDialog, handleClick,
//   assignProps: rawAssignProps, openDialog, handleCloseDialog, openAssignBed,
//   handleOpenAssignBed, handleCloseAssignBed, patient_admission_id
// } = useAddAdmission(actions, patient_number, false, admissionMatch);

// // ADD THIS
// const assignProps = rawAssignProps || {
//   openConfirmation: false,
//   handleCloseConfirmation: () => {},
//   handleBillRoom: () => {},
// };
//   // Auto-link admission to surgery when created
//   useEffect(() => {
//     if (!patient_admission_id || !surgeryId) return;
//     // Don't re-link if this is the existing admission we already know about
//     if (existingAdmissionId && String(patient_admission_id) === String(existingAdmissionId)) return;
//     surgeryService.linkAdmission({ surgery_id: surgeryId, patient_admission_id })
//       .then(() => {
//         if (onAdmissionLinked) onAdmissionLinked();
//       })
//       .catch((err) => console.error('Failed to link admission to surgery', err));
//   },  [patient_admission_id, surgeryId, existingAdmissionId, onAdmissionLinked]);

//   const { openBar, type, message } = snackbars;
//   // const {
//   //   providerProps, openNewInsurance, handleCloseNewInsurance, isPendingProvider,
//   //   handleSubmitProvider, handleOpenNewInsurance, newProviderProps,
//   //   handleSubmit, isPending
//   // } = admissionProps;
//   const {
//   providerProps, openNewInsurance, handleCloseNewInsurance, isPendingProvider,
//   handleSubmitProvider, handleOpenNewInsurance, newProviderProps,
//   handleSubmit, isPending
// } = admissionProps || {};

//   const bedButtons = (
//     <>
//       <button type="button" onClick={handleSubmit} disabled={isPending} className="sms-info-btn btn-sm btn small-btn px-2 mr-3">Yes</button>
//       <button type="button" onClick={handleCloseAssignBed} className="sms-gray-btn btn btn-sm small-btn px-2">No</button>
//     </>
//   );

//   return (
//     <Box>
//       {/* Dialogs must always render so the bed-assignment flow isn't interrupted
//           when refreshSurgery sets existingAdmissionId mid-flow */}
//       <FullScreenDialog open={openDialog} handleClose={handleCloseDialog} title="Assign Bed">
//         <AssignBed {...assignProps} />
//       </FullScreenDialog>
//       <ConfirmationDialog
//         open={!!assignProps.openConfirmation}
//         handleClose={assignProps.handleCloseConfirmation}
//         title="Make cash payment"
//         confirmItems={assignProps.handleBillRoom}
//         removeItems={assignProps.handleCloseConfirmation}
//         service="room"
//       />
//       <CustomDialog open={openAssignBed} buttons={bedButtons} handleClose={handleCloseAssignBed} title="Assign bed">
//         <p style={{ textAlign: 'center' }}>Would you like to assign a bed to this patient?</p>
//       </CustomDialog>
//       <PrintDialog
//         message="Admission Form"
//         openDialog={printDialog}
//         handleClose={handleClosePrintDialog}
//         text="Would you like to view and print this admission form"
//         handleClick={handleClick}
//         handleCancel={handleClosePrintDialog}
//       />
//       <CustomDialog open={openNewInsurance} handleClose={handleCloseNewInsurance} title="Add Provider" isContentOverflow>
//         <form onSubmit={handleSubmitProvider}>
//           <ProviderForm {...newProviderProps} />
//           <CenteredContainer>
//             <button type="submit" disabled={isPendingProvider} className="btn btn-sm sms-btn">
//               {isPendingProvider ? 'Saving...' : 'Save'}
//             </button>
//           </CenteredContainer>
//         </form>
//       </CustomDialog>
//       <MainSnackbar open={openBar} handleCloseBar={closeSnackbar} variant={type} message={message} duration={3500} />

//       {/* Show admitted notice OR the admission form */}
//       {existingAdmissionId && !openDialog && !openAssignBed ? (
//         <div style={{
//           backgroundColor: '#E8F5E9', color: '#2E7D32', border: '1px solid #C8E6C9',
//           borderRadius: 8, padding: '16px 20px', marginTop: 8, fontSize: '0.88rem', fontWeight: 500,
//         }}>
//           Patient has been admitted and assigned a bed.
//         </div>
//       ) : (
//         <div className="mt-2">
//           <DoctorAdmissionForm {...{ providerProps, handleOpenNewInsurance, admissionProps }}>
//             <button type="button" onClick={handleOpenAssignBed} className="btn btn-sm sms-btn px-4" data-testid="submitAdmissionForm">
//               Save
//             </button>
//           </DoctorAdmissionForm>
//         </div>
//       )}
//     </Box>
//   );
// };
const AdmissionSection = ({
  actions,
  snackbars,
  patient_number,
  visit_id,
  surgeryId,
  existingAdmissionId,
  onAdmissionLinked,
}) => {
  const [bedDialogClosed, setBedDialogClosed] = useState(false);
  // NEW: show admitted message right after success in this session
  const [admissionDone, setAdmissionDone] = useState(!!existingAdmissionId);

  const admissionMatch = useMemo(
    () => ({
      patient_number,
      visit_id,
      patient_admission_id: existingAdmissionId || null,
      params: {
        patient_number,
        visit_id,
        patient_admission_id: existingAdmissionId || null,
      },
    }),
    [patient_number, visit_id, existingAdmissionId]
  );

  const {
    admissionProps,
    closeSnackbar,
    printDialog,
    handleClosePrintDialog,
    handleClick,
    assignProps: rawAssignProps,
    openDialog,
    handleCloseDialog,
    openAssignBed,
    handleOpenAssignBed,
    handleCloseAssignBed,
    patient_admission_id,
  } = useAddAdmission(actions, patient_number, false, admissionMatch);

  // Keep local flag in sync if parent already has an admission
  // useEffect(() => {
  //   if (existingAdmissionId) {
  //     setAdmissionDone(true);
  //   }
  // }, [existingAdmissionId]);

  useEffect(() => {
    if (openDialog) {
      setBedDialogClosed(false);
    }
  }, [openDialog]);

  const assignProps = {
    ...(rawAssignProps || {}),
    match: {
      ...(rawAssignProps?.match || {}),
      ...admissionMatch,
      patient_admission_id:
        (rawAssignProps && rawAssignProps.patient_admission_id) ||
        patient_admission_id ||
        existingAdmissionId ||
        null,
      visit_id,
    },
    setIsSubmitted:
      rawAssignProps && typeof rawAssignProps.setIsSubmitted === 'function'
        ? rawAssignProps.setIsSubmitted
        : () => {},
    isSubmitted: (rawAssignProps && rawAssignProps.isSubmitted) || '',
    setOpenDialog: () => {
      setBedDialogClosed(true);
      setAdmissionDone(true); // ← show admitted message immediately
      if (typeof handleCloseDialog === 'function') {
        handleCloseDialog();
      }
      if (rawAssignProps && typeof rawAssignProps.setOpenDialog === 'function') {
        rawAssignProps.setOpenDialog(false);
      }
      if (onAdmissionLinked) {
        onAdmissionLinked(); // refresh surgery so existingAdmissionId is set
      }
    },
    actions: (rawAssignProps && rawAssignProps.actions) || actions,
    navigate: (rawAssignProps && rawAssignProps.navigate) || (() => {}),
  };

  // Auto-link admission → surgery
  useEffect(() => {
    if (!patient_admission_id || !surgeryId) return;
    if (
      existingAdmissionId &&
      String(patient_admission_id) === String(existingAdmissionId)
    ) {
      return;
    }
    surgeryService
      .linkAdmission({ surgery_id: surgeryId, patient_admission_id })
      .then(() => {
        if (onAdmissionLinked) onAdmissionLinked();
      })
      .catch((err) =>
        console.error('Failed to link admission to surgery', err)
      );
  }, [patient_admission_id, surgeryId, existingAdmissionId, onAdmissionLinked]);

  const { openBar, type, message } = snackbars;

  const {
    providerProps,
    openNewInsurance,
    handleCloseNewInsurance,
    isPendingProvider,
    handleSubmitProvider,
    handleOpenNewInsurance,
    newProviderProps,
    handleSubmit,
    isPending,
  } = admissionProps || {};

  const bedButtons = (
    <>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={isPending}
        className="sms-info-btn btn-sm btn small-btn px-2 mr-3"
      >
        Yes
      </button>
      <button
        type="button"
        onClick={handleCloseAssignBed}
        className="sms-gray-btn btn btn-sm small-btn px-2"
      >
        No
      </button>
    </>
  );
// const showAdmittedMessage =
//   (admissionDone || existingAdmissionId) &&
//   !openDialog &&
//   !openAssignBed &&
//   bedDialogClosed !== false
//     ? admissionDone || existingAdmissionId
//     : (admissionDone || !!existingAdmissionId) &&
//       !(!!openDialog && !bedDialogClosed) &&
//       !openAssignBed;
  return (
    <Box>
      <FullScreenDialog
        open={!!openDialog && !bedDialogClosed}
        handleClose={() => {
          setBedDialogClosed(true);
          if (typeof handleCloseDialog === 'function') {
            handleCloseDialog();
          }
        }}
        title="Assign Bed"
      >
        <AssignBed {...assignProps} />
      </FullScreenDialog>

      <ConfirmationDialog
        open={!!assignProps.openConfirmation}
        handleClose={assignProps.handleCloseConfirmation || (() => {})}
        title="Make cash payment"
        confirmItems={assignProps.handleBillRoom || (() => {})}
        removeItems={assignProps.handleCloseConfirmation || (() => {})}
        service="room"
      />

      <CustomDialog
        open={!!openAssignBed}
        buttons={bedButtons}
        handleClose={handleCloseAssignBed}
        title="Assign bed"
      >
        <p style={{ textAlign: 'center' }}>
          Would you like to assign a bed to this patient?
        </p>
      </CustomDialog>

      <PrintDialog
        message="Admission Form"
        openDialog={printDialog}
        handleClose={handleClosePrintDialog}
        text="Would you like to view and print this admission form"
        handleClick={handleClick}
        handleCancel={handleClosePrintDialog}
      />

      <CustomDialog
        open={!!openNewInsurance}
        handleClose={handleCloseNewInsurance}
        title="Add Provider"
        isContentOverflow
      >
        <form onSubmit={handleSubmitProvider}>
          <ProviderForm {...(newProviderProps || {})} />
          <CenteredContainer>
            <button
              type="submit"
              disabled={isPendingProvider}
              className="btn btn-sm sms-btn"
            >
              {isPendingProvider ? 'Saving...' : 'Save'}
            </button>
          </CenteredContainer>
        </form>
      </CustomDialog>

      <MainSnackbar
        open={openBar}
        handleCloseBar={closeSnackbar}
        variant={type}
        message={message}
        duration={3500}
      />

      {/* Show admitted notice OR the admission form */}
      {(admissionDone) &&
      !(!!openDialog && !bedDialogClosed) &&
      !openAssignBed ? (
        <div
          style={{
            backgroundColor: '#E8F5E9',
            color: '#2E7D32',
            border: '1px solid #C8E6C9',
            borderRadius: 8,
            padding: '16px 20px',
            marginTop: 8,
            fontSize: '0.88rem',
            fontWeight: 500,
          }}
        >
          Patient has been admitted and assigned a bed.
        </div>
      ) : (
        <div className="mt-2">
          <DoctorAdmissionForm
            {...{
              providerProps,
              handleOpenNewInsurance,
              admissionProps,
            }}
          >
            <button
              type="button"
              onClick={handleOpenAssignBed}
              className="btn btn-sm sms-btn px-4"
              data-testid="submitAdmissionForm"
            >
              Save
            </button>
          </DoctorAdmissionForm>
        </div>
      )}
    </Box>
  );
};
// =============================================================================
// REDUX
// =============================================================================
function mapStateToProps(state) {
  return { snackbars: state.snackbar };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SurgeryDetailPage);
